import React from 'react';
import { useGetDecisionTreeRunQuery } from '../../../../generated/core-api/graphql-react-query';
import useDecisionTreePreviewContext from '../DecisionTreePreviewContext';
import useDecisionTreeResultContext from '../DecisionTreeResultContext/useDecisionTreeResultContext';
import useDecisionTreeRunContext from '../DecisionTreeRunContext/useDecisionTreeRunContext';
import { DecisionTreeSequenceTransitionContext } from './useDecisionTreeSequenceTransitionContext';

export default function DecisionTreeSequenceTransitionContextProvider({ children }: { children: React.ReactNode }) {
  const { run, start, resume } = useDecisionTreeRunContext();
  const parentRun = useGetDecisionTreeRunQuery({ input: { id: run?.parentId ?? '' } }, { enabled: !!run?.parentId, staleTime: Infinity });
  const { setExpanded } = useDecisionTreeResultContext();
  const [transitioning, setTransitioning] = React.useState(false);

  const isPreview = useDecisionTreePreviewContext();

  const runRef = React.useRef(run);
  const parentRunRef = React.useRef(parentRun.data?.getDecisionTreeRun.sequence);
  React.useEffect(() => {
    parentRunRef.current = parentRun.data?.getDecisionTreeRun.sequence;
  }, [parentRun.data?.getDecisionTreeRun.sequence]);

  function transition() {
    setTransitioning(true);
    setTimeout(() => {
      const nextSequenceItem = parentRunRef.current?.find((option) => !option.run?.finishedAt);
      if (!nextSequenceItem) {
        resume(runRef.current.parentId);
        setExpanded(true);
      } else if (nextSequenceItem.runId) {
        resume(nextSequenceItem.runId);
      } else {
        start(nextSequenceItem.decisionTreeId, runRef.current.parentId, isPreview);
      }
      setTransitioning(false);
    }, 1200);
  }

  React.useEffect(() => {
    if (runRef.current && run) {
      if (runRef.current.id === run.id) {
        if (!runRef.current.finishedAt && run.finishedAt && run.parentId) {
          transition();
        }
      }
    }
    runRef.current = run;
  }, [run, runRef, parentRunRef]);

  const value = React.useMemo(() => ({ transitioning }), [transitioning]);

  return (
    <DecisionTreeSequenceTransitionContext.Provider value={value}>
      {children}
    </DecisionTreeSequenceTransitionContext.Provider>
  );
}
